import { useRef } from 'react';
import { BarChart3, ChevronDown, ClipboardList } from 'lucide-react';

const VIEWS = [
    {
        id: 'historique',
        action: 'Revoir mes séances',
        short: 'Mes séances',
        mode: 'Historique',
        icon: ClipboardList,
        desc: 'Retrouve chaque séance terminée, consulte tes réponses et relance un quiz quand tu veux.',
        linkLabel: 'Voir mes séances',
    },
    {
        id: 'stats',
        action: 'Comprendre ma progression',
        short: 'Ma progression',
        mode: 'Statistiques',
        icon: BarChart3,
        desc: 'Prends du recul sur tes scores, ta couverture du programme, tes points forts et tes priorités.',
        linkLabel: 'Voir mon bilan',
    },
];

function HistoryVisual() {
    return (
        <span className="mode-dock__visual results-dock__visual--history" aria-hidden="true">
            <span><i /><b /></span>
            <span><i /><b /></span>
            <span><i /><b /></span>
        </span>
    );
}

function StatsVisual({ score }) {
    const safeScore = Math.max(0, Math.min(100, Number(score) || 0));
    const bars = [
        Math.max(24, safeScore),
        Math.max(18, Math.round(safeScore * 0.72)),
        Math.max(30, Math.min(88, safeScore + 14)),
    ];

    return (
        <span className="mode-dock__visual results-dock__visual--stats" aria-hidden="true">
            {bars.map((width, index) => (
                <i key={index}><b style={{ '--results-bar': `${width}%` }} /></i>
            ))}
            {/* Arrondi : la pastille fait 44 px, « 32.2% » n'y tient pas.
                La valeur exacte reste affichée dans le libellé sous la carte. */}
            <strong>{Math.round(safeScore)}%</strong>
        </span>
    );
}

export default function ResultsDock({
    activeView,
    onViewChange,
    accentColor,
    sessionCount = 0,
    statsData = null,
}) {
    const rootRef = useRef(null);
    const active = VIEWS.find(view => view.id === activeView) || VIEWS[0];
    const globalStats = statsData?.global;
    const averageScore = Number(globalStats?.avg_score || 0);
    const statsSessions = Number(globalStats?.total_sessions || sessionCount);
    const accent = accentColor || '#6366f1';

    const metrics = {
        historique: `${sessionCount} séance${sessionCount > 1 ? 's' : ''} enregistrée${sessionCount > 1 ? 's' : ''}`,
        stats: statsSessions > 0
            ? `Score moyen : ${averageScore}% sur ${statsSessions} séance${statsSessions > 1 ? 's' : ''}`
            : 'Ton bilan apparaîtra après ta première séance',
    };

    const showContent = () => {
        onViewChange(active.id);
        window.requestAnimationFrame(() => {
            rootRef.current?.nextElementSibling?.scrollIntoView({
                behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
                block: 'start',
            });
        });
    };

    return (
        <section
            ref={rootRef}
            className="mode-dock results-dock"
            style={{ '--mode-accent': accent, '--mode-accent-base': accent }}
            aria-labelledby="results-dock-title"
        >
            <div className="mode-dock__heading">
                <span>Ton suivi</span>
                <h2 id="results-dock-title">Que veux-tu consulter&nbsp;?</h2>
            </div>

            <div className="mode-dock__cards" role="group" aria-label="Choisir une vue des résultats">
                {VIEWS.map(view => {
                    const Icon = view.icon;
                    const isActive = view.id === active.id;
                    return (
                        <button
                            key={view.id}
                            type="button"
                            aria-pressed={isActive}
                            className={`mode-dock__card ${isActive ? 'is-active' : ''}`}
                            onClick={() => {
                                onViewChange(view.id);
                                // Mobile : la carte de description est masquée, on descend directement au contenu.
                                if (window.matchMedia?.('(max-width: 640px)').matches) setTimeout(showContent, 80);
                            }}
                        >
                            <span className="mode-dock__card-head">
                                <span className="mode-dock__icon"><Icon size={20} strokeWidth={2.2} /></span>
                            </span>
                            <strong className="mode-dock__action">{view.action}</strong>
                            <strong className="mode-dock__short">{view.short || view.action}</strong>
                            <span className="mode-dock__mode">{view.mode}</span>
                            {view.id === 'historique' && <HistoryVisual />}
                            {view.id === 'stats' && <StatsVisual score={averageScore} />}
                            <span className="mode-dock__metric">{metrics[view.id]}</span>
                            <ChevronDown className="mode-dock__caret" size={19} strokeWidth={2.5} />
                        </button>
                    );
                })}
            </div>

            <div className="mode-dock__summary" aria-live="polite">
                <span>
                    <strong>{active.action}</strong>
                    {active.desc}
                </span>
                <button type="button" onClick={showContent}>
                    {active.linkLabel} <ChevronDown size={16} />
                </button>
            </div>
        </section>
    );
}
